<template>
  <VueMultiselect
    v-bind="forwardedAttrs"
    ref="multiselect"
    :model-value="modelValue"
    :multiple="multiple"
    :options="resolvedOptions"
    :track-by="resolvedTrackBy"
    :label="label"
    :searchable="true"
    :internal-search="false"
    :hide-selected="false"
    :clear-on-select="false"
    :preserve-search="true"
    :close-on-select="resolvedCloseOnSelect"
    :allow-empty="allowEmpty"
    :disabled="disabled"
    :loading="loading"
    :placeholder="placeholder"
    :options-limit="resolvedLimit"
    :open-direction="resolvedOpenDirection"
    :show-labels="false"
    :class="classes"
    :style="$attrs.style"
    @update:modelValue="handleModelValueUpdate"
    @select="handleSelect"
    @open="handleOpen"
    @close="handleClose"
    @search-change="handleSearchChange"
  >
    <template #caret="{ toggle }">
      <slot name="caret" :toggle="toggle">
        <div
          class="multiselect__select"
          @mousedown.prevent.stop="toggle"
        ></div>
      </slot>
    </template>

    <template #placeholder>
      <slot name="placeholder">
        {{ placeholder }}<span v-if="required" class="multiselect__required-marker">*</span> <slot name="logoIcon"></slot>
      </slot>
    </template>

    <template v-if="$slots.selection" #singleLabel="{ option }">
      <slot
        name="selection"
        :search="search"
        :values="option ? [option] : []"
        :isOpen="isOpen"
      />
      <span
        v-if="(labelUp || option) && placeholder"
        class="multiselect__placeholder"
        aria-hidden="true"
      >
        {{ placeholder }}<span v-if="required" class="multiselect__required-marker">*</span> <slot name="logoIcon"></slot>
      </span>
    </template>

    <template v-if="$slots.selection && multiple" #selection="{ values }">
      <slot
        name="selection"
        :search="search"
        :values="Array.isArray(values) ? values : []"
        :isOpen="isOpen"
      />
      <span
        v-if="labelUp && placeholder"
        class="multiselect__placeholder"
        aria-hidden="true"
      >
        {{ placeholder }}<span v-if="required" class="multiselect__required-marker">*</span> <slot name="logoIcon"></slot>
      </span>
    </template>

    <template #tag="{ option, remove }">
      <slot
        name="tag"
        :option="option"
        :search="search"
        :remove="remove"
        :disabled="disabled"
      >
        <span class="multiselect__tag">
          <span>{{ getOptionLabel(option) }}</span>
          <i
            v-if="!disabled"
            aria-hidden="true"
            tabindex="1"
            class="multiselect__tag-icon"
            @keypress.enter.prevent="remove(option)"
            @mousedown.prevent="remove(option)"
          ></i>
        </span>
      </slot>
    </template>

    <template #option="{ option }">
      <div @dblclick.stop="handleOptionDblClick(option)">
        <slot
          name="option"
          :option="option"
          :search="search"
        >
          <span
            class="multiselect__option-label"
            :title="getOptionLabel(option)"
            :data-marquee-text="getOptionLabel(option)"
          >{{ getOptionLabel(option) }}</span>
        </slot>
      </div>
    </template>

    <template #noResult>
      <slot name="noResult" :search="search">No elements found.</slot>
    </template>

    <template #noOptions>
      <slot name="noOptions">List is empty.</slot>
    </template>

    <template #afterList>
      <slot name="afterList" :options="resolvedOptions"></slot>
      <div v-if="$slots.opcaoEspecial" class="multiselect__element center">
        <span class="multiselect__option">
          <slot name="opcaoEspecial"></slot>
        </span>
      </div>
    </template>
  </VueMultiselect>
</template>

<script>
import { nextTick } from 'vue'
import VueMultiselect from 'vue-multiselect'

export default {
  name: 'MultiseletorWrapperLegado',
  inheritAttrs: false,
  components: {
    VueMultiselect
  },
  props: {
    modelValue: {
      type: [Array, Object, String, Number, Boolean, null],
      default: undefined
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    trackBy: {
      type: [String, Array],
      default: 'id'
    },
    label: {
      type: String,
      default: 'label'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openDirection: {
      type: String,
      default: 'below'
    },
    optionsLimit: {
      type: Number,
      default: -1
    },
    allowEmpty: {
      type: Boolean,
      default: true
    },
    closeOnSelect: {
      type: Boolean,
      default: undefined
    },
    forcedLabelUp: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:modelValue',
    'change',
    'select',
    'search-change',
    'open',
    'close',
    'dblclick',
    'load-more'
  ],
  data () {
    return {
      isOpen: false,
      search: ''
    }
  },
  beforeUnmount () {
    this.desanexarScrollInfinito()
  },
  updated () {
    if (this.isOpen) {
      this.$nextTick(() => this.marcarOpcoesComOverflow())
    }
  },
  computed: {
    classes () {
      return [
        this.$attrs.class,
        this.labelUp && 'multiselect--label-up',
        this.hasValue && 'multiselect--has-value',
        !this.multiple && 'multiselect--single',
        this.multiple && 'multiselect--multiple',
        this.isOpen ? 'multiselect--open' : 'multiselect--closed',
        this.required && 'required',
        this.error && 'error'
      ].filter(Boolean)
    },
    resolvedOptions () {
      return this.options || []
    },
    forwardedAttrs () {
      const attrs = {...this.$attrs}
      delete attrs.class
      delete attrs.style
      delete attrs.allowEmpty
      delete attrs.closeOnSelect
      delete attrs.forcedplaceholder
      delete attrs.forcedPlaceholder
      delete attrs.forcedlabelup
      delete attrs.forcedLabelUp
      delete attrs.required
      delete attrs.error
      return attrs
    },
    resolvedTrackBy () {
      return Array.isArray(this.trackBy) ? this.trackBy[0] : this.trackBy
    },
    resolvedOpenDirection () {
      return this.openDirection === 'top' ? 'above' : this.openDirection
    },
    resolvedLimit () {
      return this.optionsLimit > 0 ? this.optionsLimit : 1000
    },
    resolvedCloseOnSelect () {
      if (this.closeOnSelect !== undefined) {
        return this.closeOnSelect
      }
      return !this.multiple
    },
    labelUp () {
      if (this.multiple) {
        return this.hasValue
      }
      return this.hasValue || Boolean(this.search)
    },
    hasValue () {
      if (this.multiple) {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0
      }
      return this.modelValue !== null && this.modelValue !== undefined && this.modelValue !== ''
    }
  },
  methods: {
    getOptionLabel (option) {
      if (!option) return ''
      return option[this.label] ?? ''
    },
    handleModelValueUpdate (value) {
      this.$emit('update:modelValue', value)
      this.$emit('change', value)
    },
    handleSelect (option) {
      this.$emit('select', option, this.$attrs.id)
      if (!this.multiple && this.resolvedCloseOnSelect) {
        this.$nextTick(() => {
          this.$refs.multiselect?.deactivate?.()
          this.isOpen = false
        })
      }
    },
    handleSearchChange (query) {
      this.search = query
      this.$emit('search-change', this.search)
      this.$nextTick(() => this.marcarOpcoesComOverflow())
    },
    handleOpen () {
      this.isOpen = true
      this.$emit('open')
      // Scroll infinito: carrega a próxima página ao chegar perto do fim da lista
      // (em vez de exigir clique em "Carregar mais"). Só tem efeito em seletores
      // paginados que ouvem @load-more; nos demais é inócuo.
      this.$nextTick(() => {
        this.anexarScrollInfinito()
        this.marcarOpcoesComOverflow()
      })
    },
    handleClose () {
      this.isOpen = false
      this.desanexarScrollInfinito()
      // Zera a busca ao fechar. O multiselect usa clear-on-select=false, então o
      // termo digitado sobrevive à seleção E ao reabrir — sem isto, uma busca
      // anterior contamina a próxima (ex.: "Pedro" + "Silva" = "PedroSilva" →
      // "nada encontrado"). Emitir search-change('') reseta a lista do pai para a
      // página 0, deixando a próxima abertura limpa e completa.
      if (this.search) {
        this.clearSearch()
        this.$emit('search-change', '')
      }
      this.$emit('close')
    },
    anexarScrollInfinito () {
      const lista = this.$el?.querySelector?.('.multiselect__content-wrapper')
      if (!lista || this._onScrollLista) { return }
      this._listaScroll = lista
      this._onScrollLista = () => {
        const { scrollTop, clientHeight, scrollHeight } = lista
        // Perto do fim → pede a próxima página. O pai decide se há mais páginas e
        // se não está carregando (evita disparos repetidos); a busca atual é
        // preservada no append.
        if (scrollHeight - (scrollTop + clientHeight) < 120) {
          this.$emit('load-more')
        }
      }
      lista.addEventListener('scroll', this._onScrollLista, { passive: true })
    },
    desanexarScrollInfinito () {
      if (this._listaScroll && this._onScrollLista) {
        this._listaScroll.removeEventListener('scroll', this._onScrollLista)
      }
      this._onScrollLista = null
      this._listaScroll = null
    },
    handleOptionDblClick (option) {
      if (this.multiple) {
        this.$emit('update:modelValue', [option])
        this.$emit('change', [option])
      } else {
        this.$emit('update:modelValue', option)
        this.$emit('change', option)
        this.$emit('select', option, this.$attrs.id)
      }

      this.$emit('dblclick', option, this.$attrs.id)
      this.$nextTick(() => {
        this.$refs.multiselect?.deactivate?.()
      })
    },
    marcarOpcoesComOverflow () {
      const opcoes = this.$el?.querySelectorAll?.('.multiselect__option-label')
      if (!opcoes?.length) return

      opcoes.forEach(opcao => {
        opcao.classList.remove('multiselect__option-label--marquee')
        opcao.style.removeProperty('--multiselect-option-marquee-distance')

        if (opcao.scrollWidth > opcao.clientWidth + 1) {
          opcao.style.setProperty(
            '--multiselect-option-marquee-distance',
            `${opcao.clientWidth - opcao.scrollWidth}px`
          )
          opcao.classList.add('multiselect__option-label--marquee')
        }
      })
    },
    activate () {
      const multiselect = this.$refs.multiselect

      multiselect?.activate?.()

      nextTick(() => {
        this.$refs.multiselect?.$refs?.search?.focus?.()
      })
    },
    clearSearch () {
      if (this.$refs.multiselect && typeof this.$refs.multiselect.search !== 'undefined') {
        this.$refs.multiselect.search = ''
      }
      this.search = ''
    },
    getSearch () {
      return this.$refs.multiselect?.search ?? this.search ?? ''
    },
    setSearch (value) {
      if (this.$refs.multiselect && typeof this.$refs.multiselect.search !== 'undefined') {
        this.$refs.multiselect.search = value
      }
      this.search = value
    }
  }
}
</script>

<style>
fieldset[disabled] .multiselect {
  pointer-events: none;
}
.multiselect__spinner {
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  width: 15px;
  height: 15px;
  background: var(--input-option-box-background);
  display: block;
}
.multiselect__spinner:before,
.multiselect__spinner:after {
  position: absolute;
  content: "";
  top: 50%;
  left: 50%;
  margin: -8px 0 0 -8px;
  width: 10px;
  height: 10px;
  border-radius: 100%;
  border-color: var(--input-cor-habilitado-icone-conteudo-default) transparent transparent;
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 0 1px transparent;
}
.multiselect__spinner:before {
  animation: spinning 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
  animation-iteration-count: infinite;
}
.multiselect__spinner:after {
  animation: spinning 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
  animation-iteration-count: infinite;
}
.multiselect,
.multiselect__input,
.multiselect__single {
  font-family: var(--texto-tipografia, inherit);
  font-size: 16px;
  touch-action: manipulation;
  color: var(--input-cor-habilitado-conteudo-preenchido);
}
.multiselect {
  box-sizing: content-box;
  display: block;
  position: relative;
  width: 100%;
  margin-top: 1rem;
  text-align: left;
  color: var(--input-cor-habilitado-conteudo-preenchido);
}
.multiselect * {
  box-sizing: border-box;
}
.multiselect:focus {
  outline: none;
}
.multiselect--disabled {
  background: transparent;
  pointer-events: none;
  opacity: 0.6;
}
.multiselect--active {
  z-index: 50;
}

.multiselect--active:not(.multiselect--above) .multiselect__input {
  border-radius: 0;
}

.multiselect--active:not(.multiselect--above) .multiselect__current,
.multiselect--active:not(.multiselect--above) .multiselect__tags {
  border-bottom-left-radius: var(--raio-cartao, 16px);
  border-bottom-right-radius: var(--raio-cartao, 16px);
}
.multiselect--active:not(.multiselect--above) .multiselect__tags {
  border-bottom-left-radius: var(--raio-cartao, 16px);
  border-bottom-right-radius: var(--raio-cartao, 16px);
}
.multiselect--active .multiselect__select {
  transform: translateY(-50%) rotateZ(180deg);
}
.multiselect--above.multiselect--active .multiselect__current,
.multiselect--above.multiselect--active .multiselect__input,
.multiselect--above.multiselect--active .multiselect__tags {
  border-top-left-radius: var(--raio-cartao, 16px);
  border-top-right-radius: var(--raio-cartao, 16px);
}
.multiselect--above.multiselect--active .multiselect__tags {
  border-top-left-radius: var(--raio-cartao, 16px);
  border-top-right-radius: var(--raio-cartao, 16px);
}
.multiselect__input,
.multiselect__single {
  position: relative;
  display: inline-block;
  min-height: 20px;
  line-height: 20px;
  border: none;
  border-radius: var(--raio-cartao, 16px);
  background: transparent;
  padding: 0 0 0 0;
  width: calc(100%);
  transition: border 0.1s ease;
  box-sizing: border-box;
  margin-bottom: 0;
  vertical-align: top;
  text-overflow: ellipsis;
  overflow-x: hidden;
  white-space: nowrap;
}

.multiselect__tags-wrap {
  display: inline;
}
.multiselect__tags {
  /* Campo dropdown Pontotel: caixa arredondada, valor centralizado e altura estavel. */
  min-height: 52px;
  display: flex;
  align-items: center;
  padding: 14px 48px 10px 20px;
  border-radius: var(--raio-cartao, 16px);
  border: 1px solid var(--borda, var(--input-cor-habilitado-linha-default));
  background: var(--superficie-cartao, transparent);
  transition: all .3s ease;
  font-size: 16px;
  line-height: 22px;
}
.multiselect:hover .multiselect__tags {
  border-color: var(--input-cor-habilitado-linha-hover);
}
.multiselect__tags:focus-within {
  border-color: var(--input-cor-habilitado-linha-foco);
}
.multiselect--active .multiselect__tags {
  border-color: var(--borda-foco, var(--input-cor-habilitado-linha-clicado));
  border-width: 1.5px;
}
.multiselect.error .multiselect__tags {
  border-color: var(--input-cor-habilitado-linha-erro);
}
.multiselect__tag {
  position: relative;
  display: inline-block;
  padding: 4px 48px 0 20px;
  border-radius: var(--arredondamento-max);
  margin-right: 10px;
  color: var(--input-option-cor-habilitado-conteudo-hover);
  line-height: 1;
  background: var(--input-option-cor-habilitado-conteudo-background-hover);
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
}
.multiselect__tag-icon {
  cursor: pointer;
  margin-left: 7px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  font-weight: 700;
  font-style: normal;
  width: 22px;
  text-align: center;
  line-height: 22px;
  transition: all .2s ease;
  border-radius: var(--arredondamento-max);
}
.multiselect__tag-icon:after {
  content: "×";
  color: var(--input-option-cor-habilitado-conteudo-hover);
  font-size: 14px;
}
.multiselect__tag-icon:focus,
.multiselect__tag-icon:hover {
  background: var(--input-option-cor-habilitado-conteudo-background-foco);
}
.multiselect__tag-icon:focus:after,
.multiselect__tag-icon:hover:after {
  color: var(--input-option-cor-habilitado-conteudo-foco);
}
.multiselect__input,
.multiselect__single {
  font-size: 16px;
}
.multiselect__input {
  display: inline-block;
  width: 100%;
  min-height: 20px;
  line-height: 20px;
  padding: 0;
  background: transparent;
  margin-bottom: 0;
  vertical-align: top;
}
.multiselect__input::placeholder {
  color: transparent;
  font-size: 16px;
}
.multiselect__tag ~ .multiselect__input,
.multiselect__tag ~ .multiselect__single {
  width: auto;
}
.multiselect__input:hover,
.multiselect__single:hover {
  border-color: var(--input-cor-habilitado-linha-hover);
}
.multiselect__input:focus,
.multiselect__single:focus {
  border-color: var(--input-cor-habilitado-linha-foco);
  outline: none;
}
.multiselect__single {
  padding-left: 5px;
  margin-bottom: 0;
}
.multiselect--active .multiselect__input::placeholder {
  display: none;
}
.multiselect__single,
.multiselect__input {
  line-height: 22px;
}
.multiselect--single.multiselect--closed .multiselect__tags {
  display: flex;
  align-items: center;
}
.multiselect--single.multiselect--closed .multiselect__input {
  width: 0 !important;
  position: absolute !important;
  padding: 0 !important;
}
.multiselect--multiple.multiselect--closed .multiselect__input {
  width: 0 !important;
  position: absolute !important;
  padding: 0 !important;
}
.multiselect--single.multiselect--closed .multiselect__single {
  display: block;
  width: 100%;
  padding-left: 0;
  margin-bottom: 0;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.multiselect--single.multiselect--closed.multiselect--has-value > .multiselect__tags > .multiselect__single {
  overflow: visible;
}
.multiselect--multiple.multiselect--closed .multiselect__single {
  padding-left: 0;
  margin-bottom: 0;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.multiselect__placeholder {
  color: var(--input-cor-habilitado-conteudo-vazio);
  display: inline-block;
  margin-bottom: 0;
  padding-top: 0;
  position: absolute;
  left: 12px;
  top: 50%;
  width: calc(100% - 52px);
  transform: translateY(-50%);
  font-size: 16px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all .27s ease;
  pointer-events: none;
}
.multiselect__required-marker {
  color: var(--color-danger);
  font-size: .70em;
  margin-left: 2px;
  position: relative;
  bottom: 3px;
}
.multiselect--single .multiselect__placeholder {
  transition: none;
}
.error .multiselect__placeholder {
  color: var(--input-cor-habilitado-info-erro);
}
.multiselect--label-up .multiselect__placeholder {
  color: var(--input-cor-habilitado-label-externo-default);
  font-size: 12px;
  line-height: 14px;
  z-index: 1;
  left: 16px;
  top: 0;
  width: auto;
  max-width: calc(100% - 64px);
  padding: 0 4px;
  transform: translateY(-72%);
  background: var(--superficie-cartao, transparent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.multiselect--single.multiselect--label-up .multiselect__placeholder,
.multiselect--single.multiselect--closed.multiselect--has-value .multiselect__placeholder {
  color: var(--input-cor-habilitado-label-externo-default);
  font-size: 12px;
  line-height: 14px;
  z-index: 1;
  left: 0;
  top: 0;
  width: auto;
  max-width: 100%;
  padding: 0;
  transform: translateY(-72%);
  background: var(--superficie-cartao, transparent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.multiselect--multiple.multiselect--label-up .multiselect__placeholder,
.multiselect--multiple.multiselect--closed.multiselect--has-value .multiselect__placeholder {
  color: var(--input-cor-habilitado-label-externo-default);
  font-size: 12px;
  line-height: 14px;
  z-index: 1;
  left: 16px;
  top: 15px;
  width: auto;
  max-width: calc(100% - 64px);
  padding: 0 4px;
  transform: translateY(-72%);
  background: var(--superficie-cartao, transparent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.multiselect__select {
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  box-sizing: border-box;
  width: 48px;
  height: 52px;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  padding: 4px 8px;
  margin: 0;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: transform .2s ease;
}
.multiselect__select:before {
  position: relative;
  right: 0;
  top: 0;
  color: var(--input-cor-habilitado-icone-conteudo-default);
  margin-top: 0;
  border-style: solid;
  border-width: 5px 5px 0 5px;
  border-color: var(--input-cor-habilitado-icone-conteudo-default) transparent transparent transparent;
  content: "";
}
.multiselect__content-wrapper {
  position: absolute;
  display: block;
  background: var(--input-option-box-background);
  width: 100%;
  z-index: 100000 !important;
  max-height: 240px;
  min-height: 100px;
  overflow: auto;
  margin-top: 8px;
  padding: 8px;
  border: var(--input-option-box-espessura) solid var(--borda, var(--input-option-box-borda));
  border-radius: var(--raio-cartao, 16px);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--texto-forte, var(--input-cor-habilitado-conteudo-preenchido)) 12%, transparent);
  -webkit-overflow-scrolling: touch;
}
.multiselect__content-wrapper--hidden {
  display: none;
}
.multiselect__content-wrapper--above {
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 8px;
  border-radius: var(--raio-cartao, 16px);
}
.multiselect__content {
  display: inline-block;
  padding: 0;
  margin: 0;
  min-width: 100%;
  vertical-align: top;
  list-style: none;
  max-width: 100%;
}
.multiselect__element {
  display: block;
}
.multiselect__element.center {
  display: flex;
  align-items: center;
  justify-content: center;
}
.multiselect__option {
  display: block;
  line-height: 20px;
  text-decoration: none;
  text-transform: none;
  overflow: hidden !important;
  text-overflow: ellipsis;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  font-size: 15px;
  margin-top: 5px;
  border-radius: var(--raio-cartao-interno, 12px);
}
.multiselect__option > div {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--input-option-espacamento-m-vertical) var(--input-option-espacamento-m-horizontal);
  /* reserva o espaço do checkmark (:after e absoluto em right:0, ~45px): sem
     isto o texto longo (width:100% + ellipsis) trunca atras do check, colidindo. */
  padding-right: 44px;
  min-height: 40px;
  overflow: hidden;
}
.multiselect__option > div > span {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.multiselect__option--highlight > div > .multiselect__option-label--marquee,
.multiselect__option:hover > div > .multiselect__option-label--marquee,
.multiselect__option:focus > div > .multiselect__option-label--marquee {
  display: inline-block;
  min-width: max-content;
  width: max-content;
  overflow: visible;
  text-overflow: clip;
  animation: multiselect-option-marquee 5.5s linear infinite;
  will-change: transform;
}
.multiselect__option:after {
  top: 0;
  right: 0;
  position: absolute;
  z-index: 1;
  line-height: 40px;
  padding-right: 12px;
  padding-left: 20px;
  font-size: 17px;
}
.multiselect__option--highlight {
  background: var(--input-option-cor-habilitado-conteudo-background-hover);
  outline: none;
  color: var(--input-option-cor-habilitado-conteudo-hover);
  box-shadow: none;
}
.multiselect__option--highlight::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  border-radius: inherit;
  box-shadow: inset 0 0 0 var(--input-option-espessura-m-foco) var(--input-option-cor-habilitado-conteudo-borda-foco);
}
.multiselect__option--highlight:after {
  content: attr(data-select);
  background: var(--input-option-cor-habilitado-conteudo-background-hover);
  color: var(--input-option-cor-habilitado-conteudo-hover);
}
.multiselect__option--selected {
  background: var(--input-option-cor-habilitado-conteudo-background-clicado);
  color: var(--input-option-cor-habilitado-conteudo-clicado);
  font-weight: normal;
}
.multiselect__option--selected:after {
  content: "\2713";
  color: var(--input-option-cor-habilitado-conteudo-icone-clicado);
}
.multiselect__option--selected.multiselect__option--highlight {
  background: var(--input-option-cor-habilitado-conteudo-background-foco);
  color: var(--input-option-cor-habilitado-conteudo-foco);
  box-shadow: none;
}
.multiselect__option--selected.multiselect__option--highlight:after {
  background: var(--input-option-cor-habilitado-conteudo-background-foco);
  content: "\2713";
  color: var(--input-option-cor-habilitado-conteudo-icone-clicado);
}
.multiselect--disabled .multiselect__current,
.multiselect--disabled .multiselect__select {
  background: transparent;
  color: var(--input-cor-desabilitado-conteudo);
}
.multiselect__option--disabled {
  background: var(--input-option-box-background) !important;
  color: var(--input-option-cor-desabilitado-conteudo) !important;
  cursor: text;
  pointer-events: none;
}
.multiselect__option--group {
  background: var(--input-option-box-background);
  color: var(--input-option-cor-habilitado-conteudo-default);
}
.multiselect__option--group.multiselect__option--highlight {
  background: var(--input-option-cor-habilitado-conteudo-background-hover);
  color: var(--input-option-cor-habilitado-conteudo-hover);
}
.multiselect__option--group.multiselect__option--highlight:after {
  background: var(--input-option-cor-habilitado-conteudo-background-hover);
}
.multiselect__option--disabled.multiselect__option--highlight {
  background: var(--input-option-box-background);
}
.multiselect__option--group-selected.multiselect__option--highlight {
  background: var(--input-option-cor-habilitado-conteudo-background-foco);
  color: var(--input-option-cor-habilitado-conteudo-foco);
}
.multiselect__option--group-selected.multiselect__option--highlight:after {
  background: var(--input-option-cor-habilitado-conteudo-background-foco);
  content: attr(data-deselect);
  color: var(--input-option-cor-habilitado-conteudo-foco);
}
.multiselect__no-options,
.multiselect__no-result {
  display: block;
  padding: 12px;
  color: var(--input-cor-habilitado-conteudo-vazio);
}
.multiselect__fake-input,
.multiselect__assistive-text,
.multiselect__spacer,
.multiselect__clear,
.multiselect__tags-search-copy {
  display: none !important;
}
.center {
  text-align: center;
}
*[dir="rtl"] .multiselect {
  text-align: right;
}
*[dir="rtl"] .multiselect__tags {
  padding: 8px 8px 0 40px;
}
*[dir="rtl"] .multiselect__content {
  text-align: right;
}
*[dir="rtl"] .multiselect__select {
  right: auto;
  left: 1px;
}
*[dir="rtl"] .multiselect__option:after {
  right: auto;
  left: 0;
}
*[dir="rtl"] .multiselect__clear {
  right: auto;
  left: 12px;
}
*[dir="rtl"] .multiselect__spinner {
  right: auto;
  left: 1px;
}
@keyframes spinning {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(2turn);
  }
}
@keyframes multiselect-option-marquee {
  0%,
  16% {
    transform: translateX(0);
  }

  84%,
  100% {
    transform: translateX(var(--multiselect-option-marquee-distance, 0));
  }
}
.multiselect__single {
  padding-left: 0;
}
</style>
